package org.multiplatform.kotfolio.pages.flutter

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/flutter/solid_principles")
@Composable
fun SolidPrinciplesPage() {
    BlogLayout(
        PageProperties(
            "Solid Principles",
            "Solid is an acronym representing five design principles that aim to improve the structure and scalability of object-oriented software."
        ),
        BlogProperties("Flutter", "SOLID Principles")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "SOLID is an acronym representing five design principles that aim to improve the structure and scalability of object-oriented software.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "In the context of Dart and Flutter, adhering to SOLID principles becomes essential for ensuring maintainability and scalability as projects grow in complexity.",
                modifier = Modifier.padding(topBottom = 0.3.em)
                    .fontSize(FontSize.Medium)
            )

            SpanText(
                text = "Single Responsibility Principle (SRP)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The SRP dictates that a class handle specific responsibilities is exemplified by creating classes that focus on a single task to make the codebase more modular and easier to maintain.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each class should only do one thing. For example, if you have a class for fetching data, it should only fetch data and not also handle displaying it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Flutter, adhering to the SRP involves breaking down widgets to handle specific UI components. For instance, having separate widgets for user input and data display ensures a more modular and easily maintainable user interface.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example, the AuthenticationManager class has a single responsibility: handling user authentication. Similarly, the UserDataStorage class is dedicated to storing user data. This adheres to the SRP by keeping each class focused on a specific task.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Example of Single Responsibility Principle (SRP) in Dart

                    // Class responsible for handling user authentication
                    class AuthenticationManager {
                      bool authenticateUser(String username, String password) {
                        // Authentication logic
                        // ...
                        return true;
                      }
                    }

                    // Class responsible for user data storage
                    class UserDataStorage {
                      void saveUserData(String username, String data) {
                        // Data storage logic
                        // ...
                      }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Open/Closed Principle (OCP)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The OCP emphasizes that a class should be open for extension but closed for modification. In Dart and Flutter, developers can achieve this by using interfaces and abstract classes, allowing for new features to be added without altering existing code (Once you’ve written code, you shouldn’t need to change it to add new features. Instead, you should be able to extend the code without modifying its existing behavior.).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "This promotes a more modular approach, allowing for easy updates and additions to the application.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flutter developers can implement the OCP by utilizing composition and extension. Creating custom widgets and extending existing ones allows for the addition of new features without modifying the original code, enhancing code reusability.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example, the Shape abstract class defines the shape's contract, and concrete classes like Circle and Square extend it. The AreaCalculator class is open for extension (new shapes can be added) but closed for modification (existing code doesn't need to change), adhering to the Open/Closed Principle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Example of Open/Closed Principle (OCP) in Dart

                    // Abstract class defining the shape
                    abstract class Shape {
                      double calculateArea();
                    }

                    // Concrete classes extending the shape and implementing the area calculation
                    class Circle extends Shape {
                      double radius;

                      Circle(this.radius);

                      @override
                      double calculateArea() {
                        return 3.14 * radius * radius;
                      }
                    }

                    class Square extends Shape {
                      double side;

                      Square(this.side);

                      @override
                      double calculateArea() {
                        return side * side;
                      }
                    }

                    // Class responsible for area calculation without modification of existing code
                    class AreaCalculator {
                      double calculateTotalArea(List<Shape> shapes) {
                        double totalArea = 0.0;
                        for (var shape in shapes) {
                          totalArea += shape.calculateArea();
                        }
                        return totalArea;
                      }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Interface for playback mode
                    interface PlaybackMode {
                        fun play()
                    }

                    // Concrete class for normal playback mode
                    class NormalPlayback : PlaybackMode {
                        override fun play() {
                            println("Playing in normal mode")
                        }
                    }

                    // Concrete class for shuffle playback mode
                    class ShufflePlayback : PlaybackMode {
                        override fun play() {
                            println("Playing in shuffle mode")
                        }
                    }

                    // Concrete class for repeat playback mode
                    class RepeatPlayback : PlaybackMode {
                        override fun play() {
                            println("Playing in repeat mode")
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Liskov Substitution Principle (LSP)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Subclasses should be usable wherever their parent type is expected, without altering program correctness. The LSP states that objects of a superclass should be replaceable with objects of a subclass without affecting the correctness of the program.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "If you have a class that uses a superclass, you should be able to replace it with any of its subclasses without changing how the program works.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In Dart, adhering to the LSP ensures that subtypes can be used interchangeably with their base types. This promotes code flexibility and reusability, especially in scenarios where different classes need to share common behaviors.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the Flutter framework, designing widgets that can be easily swapped without affecting the overall functionality ensures compliance with the LSP. This flexibility is crucial for creating dynamic and customizable user interfaces.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example, the Bird class is the base class, and both Sparrow and Penguin are subclasses. The fly method is overridden in both subclasses. The letBirdFly function accepts any type of bird, demonstrating LSP compliance. Penguins, being birds, can be used interchangeably with sparrows, even though they cannot fly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Liskov Substitution Principle (LSP) example in Dart

                    // Base class representing a bird
                    class Bird {
                      void fly() {
                        print('The bird is flying.');
                      }
                    }

                    // Subclass representing a sparrow
                    class Sparrow extends Bird {
                      @override
                      void fly() {
                        print('The sparrow is flying.');
                      }
                    }

                    // Subclass representing a penguin
                    class Penguin extends Bird {
                      // Penguins cannot fly, so we override the fly method
                      @override
                      void fly() {
                        print('Penguins cannot fly.');
                      }

                      // Additional method specific to penguins
                      void swim() {
                        print('The penguin is swimming.');
                      }
                    }

                    // Function using LSP-compliant birds
                    void letBirdFly(Bird bird) {
                      bird.fly();
                    }

                    void main() {
                      // Creating instances of Sparrow and Penguin
                      var sparrow = Sparrow();
                      var penguin = Penguin();

                      // Using the function with both types of birds
                      letBirdFly(sparrow);  // Output: The sparrow is flying.
                      letBirdFly(penguin);  // Output: Penguins cannot fly.
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Interface Segregation Principle (ISP)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Clients should not be forced to depend on methods they don’t use. Prefer small, focused interfaces. Don’t force a class to use methods it doesn’t need. Instead, split interfaces into smaller, more specific ones, so each class can choose the interfaces that are relevant to it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The ISP suggests that a class should not be forced to implement interfaces it does not use. In Dart and Flutter, adhering to this principle prevents unnecessary dependencies, leading to more cohesive and readable code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "So, the developers can adhere to the ISP by creating smaller, more specialized interfaces. This allows classes to implement only the methods they need, preventing unnecessary dependencies and ensuring cleaner, more maintainable code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By creating concise and focused interfaces for widgets, Flutter developers adhere to the ISP. This approach prevents widgets from being burdened with unnecessary methods, resulting in more modular and readable code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example, the MediaPlayer interface defines basic functionalities, while the AdvancedMediaPlayer interface defines advanced functionalities. The MediaAdapter class acts as an adapter, implementing both interfaces and ensures that classes only need to implement the functionalities they require, adhering to the Interface Segregation Principle.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    interface Worker {
                      fun work()
                      fun eat()
                    }
                    
                    interface Workable {
                      fun work()
                    }

                    interface Eatable {
                      fun eat()
                    }

                    class HumanWorker : Workable, Eatable {
                      override fun work() {}
                      override fun eat() {}
                    }

                    class RobotWorker : Workable {
                      override fun work() {}
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    // Example of Interface Segregation Principle (ISP) in Dart

                    // Interface defining basic functionalities for a media player
                    abstract class MediaPlayer {
                      void play();
                      void pause();
                      void stop();
                    }

                    // Interface defining advanced functionalities for an extended media player
                    abstract class AdvancedMediaPlayer {
                      void playHD();
                      void forward();
                      void rewind();
                    }

                    // Concrete class implementing basic functionalities
                    class BasicMediaPlayer implements MediaPlayer {
                      @override
                      void play() {
                        // Play logic
                        // ...
                      }

                      @override
                      void pause() {
                        // Pause logic
                        // ...
                      }

                      @override
                      void stop() {
                        // Stop logic
                        // ...
                      }
                    }

                    // Concrete class implementing advanced functionalities
                    class AdvancedMediaPlayerImpl implements AdvancedMediaPlayer {
                      @override
                      void playHD() {
                        // HD play logic
                        // ...
                      }

                      @override
                      void forward() {
                        // Forward logic
                        // ...
                      }

                      @override
                      void rewind() {
                        // Rewind logic
                        // ...
                      }
                    }

                    // Class implementing both basic and advanced functionalities
                    class MediaAdapter implements MediaPlayer, AdvancedMediaPlayer {
                      final AdvancedMediaPlayer _advancedMediaPlayer;

                      MediaAdapter(this._advancedMediaPlayer);

                      @override
                      void play() {
                        _advancedMediaPlayer.playHD();
                      }

                      @override
                      void pause() {
                        // Basic pause logic
                        // ...
                      }

                      @override
                      void stop() {
                        // Basic stop logic
                        // ...
                      }

                      @override
                      void playHD() {
                        _advancedMediaPlayer.playHD();
                      }

                      @override
                      void forward() {
                        _advancedMediaPlayer.forward();
                      }

                      @override
                      void rewind() {
                        _advancedMediaPlayer.rewind();
                      }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Consider you have an interface called DataManager in your Android application, which includes methods for both caching and network operations. However, not all classes that use this interface need both caching and network functionalities. Instead of having a single large interface, you can split it into smaller interfaces like CacheManager and NetworkManager, each containing methods relevant to their respective responsibilities.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Interface for text messaging
                    interface TextMessaging {
                        fun sendTextMessage()
                    }

                    // Interface for voice messaging
                    interface VoiceMessaging {
                        fun sendVoiceMessage()
                    }

                    // Interface for video calling
                    interface VideoCalling {
                        fun startVideoCall()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Dependency Inversion Principle (DIP)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "High-level modules should depend on abstractions, not concrete implementations. In Dart and Flutter, this can be achieved through dependency injection, facilitating easier testing and future modifications.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Instead of depending on specific implementations, depend on abstractions. This makes your code more flexible because you can easily swap out different implementations without changing your code.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "By embracing dependency injection, Dart developers can implement the DIP effectively to make the code more adaptable to changes and easier to test.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Flutter’s dependency injection system allows developers to adhere to the DIP effectively. By relying on abstractions and interfaces, Flutter apps become more adaptable to changes, facilitating smoother maintenance and testing.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example, the DataRepository interface is a high-level module defining the contract for saving data. The DatabaseRepository is a low-level module that implements this interface. The DataProcessor class, a high-level module, depends on the abstraction (DataRepository) rather than a concrete implementation. This adheres to the Dependency Inversion Principle, allowing for flexibility and easier testing.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                     interface PaymentProcessor {
                       fun charge(amount: Double): Boolean
                     }

                     class VisaPaymentProcessor : PaymentProcessor {
                       override fun charge(amount: Double): Boolean {
                         return true
                       }
                     }

                     class StripePaymentProcessor : PaymentProcessor {
                       override fun charge(amount: Double): Boolean {
                         return true
                       }
                     }

                     // High-level module
                     class CheckoutService(private val paymentProcessor: PaymentProcessor) {
                       fun checkout(amount: Double) {
                         val success = paymentProcessor.charge(amount)
                         if (!success) error("Payment failed")
                       }
                     }
                """.trimIndent())

            KotlinCode(
                code = """
                    // Example of Dependency Inversion Principle (DIP) in Dart

                    // High-level module defining an interface
                    abstract class DataRepository {
                      void saveData(String data);
                    }

                    // Low-level module implementing the interface
                    class DatabaseRepository implements DataRepository {
                      @override
                      void saveData(String data) {
                        // Database-specific logic for saving data
                        // ...
                      }
                    }

                    // High-level module depending on the abstraction
                    class DataProcessor {
                      final DataRepository _dataRepository;

                      DataProcessor(this._dataRepository);

                      void processAndSaveData(String data) {
                        // Process data logic
                        // ...
                        _dataRepository.saveData(data);
                      }
                    }
                    """.trimIndent()
            )

            Ul {
                Li(Modifier.padding(top = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Suppose you have a class called DatabaseManager responsible for interacting with a database in your Android application. Instead of having other classes directly depend on DatabaseManager, you can introduce an abstraction like an interface (DatabaseInterface) that defines the operations needed by dependent classes. Then, DatabaseManager implements this interface, and dependent classes depend on the interface rather than the concrete implementation. This allows for easier swapping of database implementations in the future without modifying dependent classes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    // Interface for database operations
                    interface DatabaseService {
                        fun saveData(data: String)
                    }

                    // Concrete class using SQLite database
                    class SQLiteDatabaseService : DatabaseService {
                        override fun saveData(data: String) {
                            println(data)
                        }
                    }

                    // Concrete class using Room database
                    class RoomDatabaseService : DatabaseService {
                        override fun saveData(data: String) {
                            println(data)
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}